/**
 * A forge's publishing options (GET/PUT /forges/:id/publish-settings) and its
 * icon. Every call resolves to the fresh view, which replaces the cache.
 * Errors reject so the dialog can show them where the user is looking.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { type ForgePublishOptions, type ForgePublishSettingsView } from '@/lib/api';
import { forgeQueryKeys } from '../api/queryKeys';

/** Mirrors the icon pipeline's limits. */
export const FORGE_ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
export const FORGE_ICON_MAX_BYTES = 5 * 1024 * 1024;

export function useForgePublishSettings(forgeId: string | null, enabled = true) {
  const queryClient = useQueryClient();
  const key = forgeQueryKeys.publishSettings(forgeId ?? '');
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.getForgePublishSettings(forgeId!),
    enabled: !!forgeId && enabled,
  });

  const put = useCallback(
    (view: ForgePublishSettingsView) => queryClient.setQueryData(key, view),
    [queryClient, key],
  );

  const save = useCallback(
    async (options: ForgePublishOptions) =>
      put(await api.saveForgePublishSettings(forgeId!, options)),
    [forgeId, put],
  );

  const uploadIcon = useCallback(
    async (file: File) => {
      if (!FORGE_ICON_TYPES.includes(file.type)) {
        throw new Error('Use a PNG, JPEG, WebP or SVG image.');
      }
      if (file.size > FORGE_ICON_MAX_BYTES) throw new Error('The icon must be 5 MB or smaller.');
      const { uploadUrl, stagingKey } = await api.getForgeIconUploadUrl(
        forgeId!,
        file.type,
        file.size,
      );
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error('The icon could not be uploaded. Try again.');
      put(await api.confirmForgeIcon(forgeId!, stagingKey));
    },
    [forgeId, put],
  );

  const removeIcon = useCallback(
    async () => put(await api.removeForgeIcon(forgeId!)),
    [forgeId, put],
  );

  return {
    view: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    save,
    uploadIcon,
    removeIcon,
  };
}
